import React from 'react';
import { Form, AutoSaveInput } from '@blueprintnyc/bpnyc-common';
import { Toggle } from '@blueprintnyc/bpnyc-common-admin';
import ColorInput from 'components/ColorInput';
import EventDateTimePicker from 'components/EventDateTimePicker';
import EventTimeHeader from 'components/EventTimeHeader';
import {
	COUNTDOWN_COLOR_FIELDS,
	COUNTDOWN_VARIANTS,
	COUNTDOWN_VARIANT_COLORS,
	TRANSPARENT,
	asText,
	formatCountdownTarget,
	resolveCountdownColor,
	resolveCountdownTarget,
	resolveCountdownVariant,
} from 'components/Widgets/countdown/countdown';
import useCountdown from 'components/Widgets/countdown/useCountdown';
import WidgetOptionsForm from './WidgetOptionsForm';
import WidgetOptionGroupPermissions from './WidgetOptionGroupPermissions';
import './CountdownWidgetOptions.scss';

const TITLE_POSITIONS = ['above', 'below'];

const TargetSummary = ({ event, targetMs }) => {
	const countdown = useCountdown(targetMs);
	if (targetMs === null) return <p className="countdown-target-summary">Pick a date and time.</p>;
	const hasPassed = countdown?.mode === 'ended';
	return (
		<p className={`countdown-target-summary${hasPassed ? ' has-passed' : ''}`}>
			Counts down to <b>{formatCountdownTarget(event, targetMs)}</b>.
			{hasPassed && ' This time has passed. Attendees see 0 hours 0 min.'}
		</p>
	);
};

const CountdownWidgetOptions = props => {
	const {
		event,
		widgetToUpdate,
		widgetProps,
		handleChange,
		handlePropChange,
		handleGroupChange,
		targetKeys,
	} = props;
	const variant = resolveCountdownVariant(widgetProps);
	const targetMs = resolveCountdownTarget(widgetProps);

	const handleFormChange = values =>
		handleChange(
			values.variant && values.variant !== widgetProps.variant
				? { ...values, ...COUNTDOWN_VARIANT_COLORS[values.variant] }
				: values
		);

	return (
		<WidgetOptionsForm initialValues={widgetToUpdate.props} handleChange={handleFormChange}>
			<Form.Item label="Style" name="variant">
				<Toggle toggleOptions={COUNTDOWN_VARIANTS} />
			</Form.Item>
			<h4 className="admin-form-item-label">
				<EventTimeHeader event={event} referenceDate={widgetProps.targetTime || undefined} />
			</h4>
			<div className="countdown-target-picker">
				<EventDateTimePicker
					event={event}
					value={widgetProps.targetTime}
					placeholder="Select date and time"
					onChange={picked => handlePropChange('targetTime')(picked ? picked.toISOString() : null)}
				/>
			</div>
			<TargetSummary event={event} targetMs={targetMs} />
			<Form.Item label="Title (optional)" name="title">
				<AutoSaveInput bufferTime={0} />
			</Form.Item>
			{asText(widgetProps.title) && (
				<Form.Item label="Title position" name="titlePosition">
					<Toggle toggleOptions={TITLE_POSITIONS} />
				</Form.Item>
			)}
			<Form.Item
				label="Message when it hits zero (optional)"
				name="endMessage"
				placeholder="It's happening now!"
			>
				<AutoSaveInput bufferTime={0} />
			</Form.Item>
			<Form.Item label="Show target date & time?" name="showTargetTime">
				<Form.TwoOptionSelect />
			</Form.Item>
			<div className="countdown-color-fields">
				{COUNTDOWN_COLOR_FIELDS[variant]
					.filter(
						({ hiddenWhenTransparent }) =>
							!(hiddenWhenTransparent && widgetProps.backgroundColor === TRANSPARENT)
					)
					.map(({ prop, label, allowTransparent }) => (
						<div className="countdown-color-field" key={prop}>
							<h4 className="admin-form-item-label">{label}</h4>
							<ColorInput
								color={resolveCountdownColor(widgetProps, prop)}
								onChange={hex => handlePropChange(prop)(hex)}
								eventID={event.id}
								allowTransparent={allowTransparent}
							/>
						</div>
					))}
			</div>
			<WidgetOptionGroupPermissions
				event={event}
				handleGroupChange={handleGroupChange}
				targetKeys={targetKeys}
			/>
		</WidgetOptionsForm>
	);
};

export default CountdownWidgetOptions;
